import {useQueryClient} from '@tanstack/react-query';
import {createApiQuery, useApiMutation} from './api';
import {
  AuthIntegration,
  CheckWebIntentScriptDetection,
  DeleteIntegration,
  DisconnectIntegration,
  EnableIntegration,
  GetIntegration,
  GetIntegrations,
  UpdateIntegration,
} from '../../shared/api/definitions/integration';
import {IntegrationType} from '../../shared/integrations/common';

export const IntegrationKeys = {
  all: ['integrations'],
  get: (integrationType: IntegrationType) => [
    ...IntegrationKeys.all,
    integrationType,
  ],
} as const;

export const useIntegrations = createApiQuery(
  GetIntegrations,
  () => IntegrationKeys.all
);

export const useIntegration = createApiQuery(GetIntegration, ({type}) =>
  IntegrationKeys.get(type)
);

export function useUpdateIntegration() {
  const queryClient = useQueryClient();
  return useApiMutation(UpdateIntegration, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: IntegrationKeys.all,
      });
    },
  });
}

export function useDisconnectIntegration() {
  const queryClient = useQueryClient();
  return useApiMutation(DisconnectIntegration, {
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({
        queryKey: IntegrationKeys.get(name),
      });
    },
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();
  return useApiMutation(DeleteIntegration, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: IntegrationKeys.all,
      });
    },
  });
}

export function useAuthIntegration() {
  return useApiMutation(AuthIntegration);
}

export function useEnableIntegration() {
  const queryClient = useQueryClient();
  return useApiMutation(EnableIntegration, {
    onSuccess: () => {
      // we want to clear out any stale errors from integrations that were
      // deleted and re-created, so we reset insteat of invalidate. if we wanted
      // we could just reset the specific query that was enabled
      queryClient.resetQueries({
        queryKey: IntegrationKeys.all,
      });
    },
  });
}

export function useCheckWebIntentScriptDetection() {
  const queryClient = useQueryClient();
  return useApiMutation(CheckWebIntentScriptDetection, {
    onSuccess: (data) => {
      if (data.detectedAt) {
        queryClient.invalidateQueries({
          queryKey: IntegrationKeys.all,
        });
      }
    },
  });
}
